import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Security } from '@okta/okta-react';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { oktaAuth } from './lib/okta-auth';
import { SecureRoute } from './components/SecureRoute';
import { FeatureFlagsProvider, FeatureFlagRoute } from './lib/feature-flags';
import { LoginCallback } from './pages/LoginCallback';
import { Login } from './pages/Login';
import { DashboardLayout } from './pages/DashboardLayout';
import { Overview } from './pages/Overview';
import AgentsPage from './pages/AgentsPage';
import AgentDetailPage from './pages/AgentDetail';
import { ResourcesPage } from './pages/ResourcesPage';
import { AuditLog } from './pages/AuditLog';
import { EventLog } from './pages/EventLog';
import DCRSettings from './pages/DCRSettings';
import ServiceAppSettings from './pages/ServiceAppSettings';
import UserSessions from './pages/UserSessions';

// 0.16.x-specific pages — lazy-loaded; modules to be ported separately
const PoliciesPage = lazy(() => import('./pages/PoliciesPage'));
const PoliciesNewPage = lazy(() => import('./pages/PoliciesNewPage'));
const NLOperationsPage = lazy(() => import('./pages/NLOperationsPage'));
const SimulatorPage = lazy(() => import('./pages/SimulatorPage'));
const PolicyDetailPage = lazy(() => import('./pages/PolicyDetailPage'));
const ToolsPage = lazy(() => import('./pages/ToolsPage'));
const ResourceDetailPage = lazy(() => import('./pages/ResourceDetailPage'));
const ResourceAuditPage = lazy(() => import('./pages/ResourceAuditPage'));
const AgentAuditPage = lazy(() => import('./pages/AgentAuditPage'));
const ApiConnectionsPage = lazy(() => import('./pages/ApiConnectionsPage'));

const PageFallback = () => (
  <div className="flex items-center justify-center min-h-[40vh]">
    <div className="text-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3"></div>
      <p className="text-muted-foreground text-sm">Loading...</p>
    </div>
  </div>
);

function App() {
  const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri: string) => {
    window.location.replace(toRelativeUrl(originalUri || '/dashboard', window.location.origin));
  };

  return (
    <BrowserRouter>
      <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
        <FeatureFlagsProvider>
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/callback" element={<LoginCallback />} />

            <Route element={<SecureRoute />}>
              <Route
                path="/dashboard"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <DashboardLayout />
                  </Suspense>
                }
              >
                <Route index element={<Overview />} />
                <Route path="agents" element={<AgentsPage />} />
                <Route path="agents/:agentId" element={<AgentDetailPage />} />
                <Route path="agents/:agentId/audit" element={<AgentAuditPage />} />
                <Route path="resources" element={<ResourcesPage />} />
                <Route path="resources/:resourceName" element={<ResourceDetailPage />} />
                <Route path="resources/:resourceName/audit" element={<ResourceAuditPage />} />
                <Route path="api-connections" element={<ApiConnectionsPage />} />
                <Route path="tools" element={<ToolsPage />} />
                <Route
                  path="policies"
                  element={
                    <FeatureFlagRoute flag="cedarEnabled">
                      <PoliciesPage />
                    </FeatureFlagRoute>
                  }
                />
                <Route
                  path="policies/new"
                  element={
                    <FeatureFlagRoute flag="cedarEnabled">
                      <PoliciesNewPage />
                    </FeatureFlagRoute>
                  }
                />
                <Route
                  path="policies/nl-operations"
                  element={
                    <FeatureFlagRoute flag="cedarEnabled">
                      <NLOperationsPage />
                    </FeatureFlagRoute>
                  }
                />
                <Route
                  path="policies/simulator"
                  element={
                    <FeatureFlagRoute flag="cedarEnabled">
                      <SimulatorPage />
                    </FeatureFlagRoute>
                  }
                />
                <Route
                  path="policies/:policyId"
                  element={
                    <FeatureFlagRoute flag="cedarEnabled">
                      <PolicyDetailPage />
                    </FeatureFlagRoute>
                  }
                />
                <Route path="audit" element={<AuditLog />} />
                <Route path="event-log" element={<EventLog />} />
                <Route path="sessions" element={<UserSessions />} />
                <Route path="settings/dcr" element={<DCRSettings />} />
                <Route path="settings/service-app" element={<ServiceAppSettings />} />
              </Route>
            </Route>
          </Routes>
        </FeatureFlagsProvider>
      </Security>
    </BrowserRouter>
  );
}

export default App;
